import React, { useState, useEffect, useCallback, useMemo } from 'react'
import instrument from '@adc/instrument'
// @ts-ignore no types
import { get } from 'lodash'
// @ts-ignore no types
import { SHORT_CRITICAL_SESSION_EXPIRED } from '@adc/core-utils/src/constants/errors'
import {
  withApplicationNotificationsContext,
  NOTIFICATION_ROLES,
  // @ts-ignore no types
} from '@adc/ui-adc'
import type {
  AuctionBlueprintOnlineReactiveArgs,
  AuctionBlueprintScheduledOnlineReactiveArgs,
  primitives,
} from '@adc/blueprint'
import { FeatureFlags, useListingContext } from 'common/context/listing'
import * as graph from '@adc/sdk'
import * as errorMessages from 'common/constants/errorMessages'
import analytics from 'common/services/analytics'
import { TRANSIENT_MESSAGE_BID_WAS_PLACED } from 'common/statics'
import { formatMoneyWithCentsExceptZero } from 'common/utils/formatters'
import { isTPSOnline } from 'common/utils/listing'
import interpolate from 'common/utils/interpolate'
import { ZERO_DOLLARS } from 'common/components/bidBanner/constants'
import { ApiMapperBiddingError } from '../../../errors'
import { numberNoDecimals, numberWithDecimals } from '../../../utils/index'

type AuctionUser = primitives.AuctionUser

const CONFIRM_EMAIL_CONTENT = (
  <div>
    <p>
      Check your email to confirm your account. Then, log out of your account
      and log back in so we can confirm your status.
    </p>
    <p>This must be done in order to place bids.</p>
  </div>
)

const REFRESH_TOKEN_CONTENT =
  'Your bid cannot be placed at this time. Please log out of your account and log back in so we can confirm your status.'

const WARNING_DELAY_IN_MS = 500

const getErrorMessage = (errorCode: string) =>
  get(errorMessages, errorCode, errorMessages.defaultMessage)

export interface BidFormProps {
  listing: AuctionBlueprintOnlineReactiveArgs['listing']
  segment: AuctionBlueprintScheduledOnlineReactiveArgs['segment']
  user: AuctionUser
  notifications: any
  shouldShowOneAwayMessage: boolean
  children: any
  performBidVerificationAction: any
  featureFlags: FeatureFlags
}

const BidForm = ({
  user,
  notifications,
  shouldShowOneAwayMessage,
  children,
  performBidVerificationAction,
  featureFlags,
}: BidFormProps) => {
  const { listing, segment } = useListingContext()
  const [bidAmount, setBidAmount] = useState(0)
  const [error, setError] = useState(false)
  const [touched, setTouched] = useState(false)
  const [isBidConfirmationOpen, setIsBidConfirmationOpen] = useState(false)
  const [isPerformingBid, setIsPerformingBid] = useState(false)
  const [shouldShowWarning, setShouldShowWarning] = useState(false)

  const isProxyBiddingActive = useMemo(
    () => segment?.__methods__.isProxyBiddingActive(listing),
    [listing, segment]
  )

  const isTPSOnlineListing = isTPSOnline(listing, segment)

  const getNextBidAmount = useCallback(() => {
    const nextBidAmount = isProxyBiddingActive
      ? segment?.__methods__.getNextProxyBidAmount(listing)
      : segment?.__methods__.getNextBidAmount()
    if (nextBidAmount) {
      return nextBidAmount
    } else {
      throw new Error('Next bid amount is not defined')
    }
  }, [isProxyBiddingActive, listing, segment?.__methods__])

  const handleBidButtonClick = (openBidConfirmationModalCallback: any) => {
    if (user.logged_in && user.status === graph.enums.UserStatus.Confirmed) {
      // TODO: remove this after removing all places that need old modal, at the time of this commit, only matchBidModal is using this
      setIsBidConfirmationOpen(true)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      openBidConfirmationModalCallback()
    } else if (user.logged_in) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      notifications.overlayBanner.open({
        message: CONFIRM_EMAIL_CONTENT,
        title: 'Confirm Your Email',
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        role: NOTIFICATION_ROLES.ERROR,
        centered: false,
      })
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      notifications.overlayBanner.open({
        message: REFRESH_TOKEN_CONTENT,
        title: 'Refresh Token',
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        role: NOTIFICATION_ROLES.ERROR,
        centered: false,
      })
    }
  }

  const hideBidConfirmation = () => {
    setIsBidConfirmationOpen(false)
  }

  const showBidAttemptToast = useCallback(
    (message: any, type: any) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (type === NOTIFICATION_ROLES.SUCCESS) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        notifications.overlayBanner.open({
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          message,
          title: 'Thank You!',
        })
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      } else if (type === NOTIFICATION_ROLES.ERROR) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        notifications.overlayBanner.open({
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          message,
          title: 'Bid Error',
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          role: NOTIFICATION_ROLES.ERROR,
        })
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    [notifications.overlayBanner]
  )

  const handleSuccess = useCallback(
    (res: any) => {
      hideBidConfirmation()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const displayBidAmount = formatMoneyWithCentsExceptZero(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        res.current_bid_amount || bidAmount,
        ZERO_DOLLARS
      )
      const message = interpolate(TRANSIENT_MESSAGE_BID_WAS_PLACED, [
        displayBidAmount,
      ])
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      showBidAttemptToast(message, NOTIFICATION_ROLES.SUCCESS)
      analytics.bidBanner.confirmBid.success()

      instrument.increment('bidding_analytics', 1, [
        `status:success`,
        `version:v2`,
        `action:bid`,
        `scope:handleSuccess`,
      ])
    },
    [bidAmount, showBidAttemptToast]
  )

  const handleProxyBidSuccess = useCallback(
    (proxyBidAmount: number) => {
      hideBidConfirmation()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const displayBidAmount = formatMoneyWithCentsExceptZero(
        proxyBidAmount,
        ZERO_DOLLARS
      )
      const message = interpolate(TRANSIENT_MESSAGE_BID_WAS_PLACED, [
        displayBidAmount,
      ])
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      showBidAttemptToast(message, NOTIFICATION_ROLES.SUCCESS)
      analytics.bidBanner.confirmProxyBid.success()
      instrument.increment('bidding_analytics', 1, [
        `status:success`,
        `version:v2`,
        `action:proxy_bid`,
        `scope:handleProxyBidSuccess`,
      ])
    },
    [showBidAttemptToast]
  )

  const cleanUp = () => {
    setIsPerformingBid(false)
    setTouched(false)
  }

  const updateBidAmountProgrammatically = useCallback(() => {
    const bidAmount = getNextBidAmount()
    setBidAmount(bidAmount)
    setError(false)
    setShouldShowWarning(false)
    setTouched(false)
  }, [getNextBidAmount])

  const performBidV2 = useCallback(() => {
    const handleError = (e: any, action = '', isProxy: boolean) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const err = new ApiMapperBiddingError(e)
      hideBidConfirmation()
      if (isProxy) {
        analytics.bidBanner.confirmProxyBid.fail()
      } else {
        analytics.bidBanner.confirmBid.fail()
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const errMessage = get(err, 'errorBody.message')
      if (errMessage === SHORT_CRITICAL_SESSION_EXPIRED) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        performBidVerificationAction(performBidV2)
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const errorCode = get(err, 'code')

        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        const errorMessage = getErrorMessage(errorCode)

        instrument.increment(`bidding_analytics`, 1, [
          `status:failure`,
          `version:v2`,
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          `error: ${errorCode || ''}`,
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          `http_code: ${get(err, 'errorBody.status')}`,
          `action:${action}`,
          `scope:handleError`,
        ])

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        showBidAttemptToast(errorMessage, NOTIFICATION_ROLES.ERROR)

        // If the bid attempt failed for a reason other than short-term session expiration,
        // the most likely cause is that another user placed a bid
        // while the first user's BidConfirmationModal was open.
        updateBidAmountProgrammatically()
      }
    }

    setIsPerformingBid(true)

    if (isProxyBiddingActive) {
      segment?.__methods__
        .upsertListingOnlineMaxBid({
          max_bid_amount: bidAmount,
          bidder_party_id: (user.logged_in && user.party_id) || '',
        })
        .then(() => handleProxyBidSuccess(bidAmount))
        .catch((e: any) => handleError(e, 'proxy_bid', true))
        .finally(cleanUp)
    } else {
      if (shouldShowOneAwayMessage) {
        instrument.increment('bidding.action', 1, [
          `action:regular_bid`,
          `phase:one_away`,
        ])
      }

      segment?.__methods__
        .submitOnlineRegularBid({
          bid_amount: bidAmount,
          // @ts-ignore type says mismatch but its correct
          type: graph.enums.OnlineAuctionSegmentBidType.Regular,
          bidder_party_id: (user.logged_in && user.party_id) || '',
        })
        .then((res: any) => handleSuccess(res))
        .catch((e: any) => handleError(e, 'bid', false))
        .finally(cleanUp)
    }
  }, [
    bidAmount,
    handleProxyBidSuccess,
    handleSuccess,
    isProxyBiddingActive,
    performBidVerificationAction,
    segment?.__methods__,
    shouldShowOneAwayMessage,
    showBidAttemptToast,
    updateBidAmountProgrammatically,
    user,
  ])

  const onChange = (e: any) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const inputValue = e.target.value || 0
    const bidAmount = parseFloat(
      isTPSOnlineListing
        ? numberWithDecimals(inputValue)
        : // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          numberNoDecimals(inputValue)
    )
    setTouched(true)
    if (bidAmount >= getNextBidAmount()) {
      setBidAmount(bidAmount)
      setError(false)
      setShouldShowWarning(false)
    } else {
      setBidAmount(bidAmount)
      setError(true)
      setTimeout(() => setShouldShowWarning(error), WARNING_DELAY_IN_MS)
    }
  }

  useEffect(() => {
    if (!touched) {
      updateBidAmountProgrammatically()
    }
  }, [
    touched,
    segment?.current_user_max_bid?.max_bid_amount,
    segment?.current_user_highest_bid,
    updateBidAmountProgrammatically,
  ])

  const warning = error && shouldShowWarning

  const childrenProps = {
    bidAmount,
    handleBidButtonClick,
    hasError: error,
    hideBidConfirmation,
    isBidConfirmationOpen,
    isPerformingBid,
    isProxyBiddingActive,
    onChange,
    performBid: performBidV2,
    warning,
    listing,
    segment,
    featureFlags,
    user,
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call
  return children(childrenProps)
}

export { BidForm }
// eslint-disable-next-line @typescript-eslint/no-unsafe-call
export default withApplicationNotificationsContext(BidForm)
